import type { CSSProperties } from 'react';
import { cn } from '@/lib/utils';

/**
 * Local mirror of `useVoiceAssistant().state` from `@livekit/components-react`,
 * plus `failed` (Storybook) for presentational use. Defined locally so this view
 * module has zero LiveKit imports — the eager critical-path bundle must not pull
 * `@livekit/components-react`.
 *
 * If LiveKit adds a new state value, the contract test at
 * `tests/unit/components/v2/AnakaBarVisualizerView.types.test.ts` will fail to
 * compile.
 */
/** Aligned with `@livekit/components-react` `AgentState` plus `error` for app `VoiceState`. */
export type AnakaBarVisualizerViewState =
  | 'disconnected'
  | 'connecting'
  | 'pre-connect-buffering'
  | 'failed'
  | 'initializing'
  | 'idle'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'error';

/**
 * Three-state waveform preset from the handoff design (FR-016).
 * Consumed by the orchestrator via `mapVoiceVisualState`; takes precedence
 * over the legacy `state` prop when supplied.
 *
 * - `baseline`  — static dotted baseline (Anaka idle/ready)
 * - `thinking`  — slower (~1.4 s), shorter bars, purple palette
 * - `responding` — faster (~0.55 s), taller bars, purple→blue palette
 */
export type WaveformState = 'baseline' | 'thinking' | 'responding';

export interface AnakaBarVisualizerViewProps {
  /**
   * Legacy LiveKit-aligned state prop. Maps internally to one of the three
   * waveform presets. Superseded by `waveform` when both are supplied.
   */
  state?: AnakaBarVisualizerViewState;
  /**
   * Explicit waveform preset from `mapVoiceVisualState().waveform` (FR-016).
   * When present, overrides any derivation from `state`.
   */
  waveform?: WaveformState;
  /**
   * Dense layout: narrower bars and tighter gap — used inside the inline
   * (detailed-mode) voice bar. Matches the handoff `dense` prop.
   */
  dense?: boolean;
  /** Reserved for API stability; the view only renders the static bar strip. */
  trackRef?: unknown;
  className?: string;
}

export const BAR_COUNT = 6;

// Tailwind arbitrary variants re-declare the layout + colour that LiveKit's
// `<BarVisualizer>` (`@livekit/components-react`) normally gets from
// `@livekit/components-styles` — which this app does NOT import. Without them the
// live equaliser is invisible ("flat"), which is exactly the long-standing bug:
//
//  1. The wrapper `.lk-audio-bar-visualizer` has no flexbox and no definite
//     height, so the bars' inline `height: N%` (LiveKit sizes bars by percent of
//     the container) resolves against an auto height → 0px tall.
//  2. Each bar `.lk-audio-bar` has no width → 0px wide → nothing to see.
//
// CRITICAL: LiveKit v2 renders bars as `<span class="lk-audio-bar"
// data-lk-bar-index="…">` — there is NO `data-lk-bar` attribute. The previous
// selector `[data-lk-bar]` matched none of them, so the width/colour never
// applied and the equaliser stayed dead. We target the real class names here.
// (The static `AnakaBarVisualizerView` fallback styles its own `data-lk-bar`
// spans entirely inline, so it is unaffected by this selector change.)
export const BAR_CLASSES =
  // Wrapper: centered flex row with a definite height so bar percentages resolve.
  '[&_.lk-audio-bar-visualizer]:flex ' +
  '[&_.lk-audio-bar-visualizer]:h-full ' +
  '[&_.lk-audio-bar-visualizer]:w-full ' +
  '[&_.lk-audio-bar-visualizer]:items-center ' +
  '[&_.lk-audio-bar-visualizer]:justify-center ' +
  '[&_.lk-audio-bar-visualizer]:gap-[3px] ' +
  // Bars: LiveKit sets only the inline height — we supply width + colour + shape.
  '[&_.lk-audio-bar]:w-[3px] ' +
  '[&_.lk-audio-bar]:bg-primary ' +
  '[&_.lk-audio-bar]:rounded-full ' +
  '[&_.lk-audio-bar]:transition-[height] ' +
  '[&_.lk-audio-bar]:duration-150';

/**
 * Bar colour when the *user* (local mic) is the active speaker, so it's
 * immediately clear who is talking. Teal — distinct from Anaka's primary purple.
 * `!` (important) so it wins over the `bg-primary` in {@link BAR_CLASSES} when
 * both are applied to the same container.
 */
export const USER_BAR_COLOR_CLASS = '[&_.lk-audio-bar]:!bg-[#2DD4BF]';

/** Who the single shared waveform should currently represent. */
export type ActiveSpeaker = 'anaka' | 'user';

/**
 * Resolve whose audio the waveform should reflect. The user wins only when
 * they're speaking, Anaka isn't, and their mic is actually available; Anaka
 * takes precedence otherwise (including the neither-speaking idle case, where
 * her track drives the ambient animation). Pure + LiveKit-free so it's unit
 * testable without a Room context.
 */
export function resolveActiveSpeaker(opts: {
  anakaSpeaking: boolean;
  userSpeaking: boolean;
  hasMic: boolean;
}): ActiveSpeaker {
  return opts.userSpeaking && !opts.anakaSpeaking && opts.hasMic ? 'user' : 'anaka';
}

// ─── Handoff waveform palettes (FR-016) ───────────────────────────────────
const PALETTE_THINKING: readonly string[] = ['#C084FC', '#A855F7', '#7C3AED', '#A855F7'];
const PALETTE_RESPONDING: readonly string[] = ['#7C3AED', '#3B82F6', '#A855F7', '#60A5FA'];

/**
 * Derive the WaveformState from the legacy LiveKit-aligned `state` prop.
 * Matches the same mapping used in `StaticBarFallback` prior to the restyle.
 */
function legacyStateToWaveform(state: AnakaBarVisualizerViewState | undefined): WaveformState {
  if (state === 'thinking' || state === 'listening') return 'thinking';
  if (state === 'speaking') return 'responding';
  return 'baseline';
}

/**
 * Build bar descriptors for the animated thinking/responding presets.
 * Uses the same seed formula as the handoff Waveform component.
 */
function buildBars(
  count: number,
  height: number,
  heightMul: number,
  palette: readonly string[],
): Array<{ h: number; c: string }> {
  return Array.from({ length: count }, (_, i) => {
    const seed = (i * 13) % 100;
    const baseH = 6 + (Math.sin(i * 0.7) + 1) * (height / 2.6) + seed / 8;
    return {
      h: Math.min(height, baseH * heightMul),
      c: palette[i % palette.length],
    };
  });
}

function StaticBarFallback({
  state,
  waveform: waveformProp,
  dense,
  className,
}: Pick<AnakaBarVisualizerViewProps, 'state' | 'waveform' | 'dense' | 'className'>) {
  // `waveform` prop takes priority; fall back to mapping the legacy `state`.
  const waveform: WaveformState = waveformProp ?? legacyStateToWaveform(state);

  // Height matches the container h-10 (40px) used externally; passed into bar
  // calculation so heightMul produces correct absolute pixel heights.
  const height = 40;
  const barWidth = dense ? 2 : 3;
  const gap = dense ? 1.5 : 2.5;

  // ── Baseline (ready / idle / disconnected) ────────────────────────────
  if (waveform === 'baseline') {
    // Also handle the legacy `connecting` / `initializing` shimmer:
    // when the caller has only supplied `state` (not `waveform`) and the state
    // is one of the pre-connect states, use the shimmer animation.
    const isConnecting =
      !waveformProp &&
      (state === 'connecting' || state === 'initializing' || state === 'pre-connect-buffering');

    return (
      <div
        className={cn('flex items-center justify-center', BAR_CLASSES, className)}
        style={{ height, gap }}
        aria-hidden="true"
      >
        {Array.from({ length: BAR_COUNT }, (_, i) => {
          const style: CSSProperties = isConnecting
            ? {
                width: barWidth,
                height: barWidth,
                display: 'inline-block',
                background: 'var(--muted-foreground, #6B7280)',
                borderRadius: '50%',
                opacity: 0.32,
                animation: `anaka-bar-shimmer 1.5s ease-in-out ${i * 0.15}s infinite`,
              }
            : {
                width: barWidth,
                height: barWidth,
                display: 'inline-block',
                background: 'var(--muted-foreground, #6B7280)',
                borderRadius: '50%',
                opacity: 0.32,
              };
          return <span key={i} data-lk-bar style={style} />;
        })}
      </div>
    );
  }

  // ── Thinking or Responding ─────────────────────────────────────────────
  const palette = waveform === 'thinking' ? PALETTE_THINKING : PALETTE_RESPONDING;
  const heightMul = waveform === 'thinking' ? 0.45 : 0.72;
  const animDur = waveform === 'thinking' ? '1.4s' : '0.55s';
  const bars = buildBars(BAR_COUNT, height, heightMul, palette);

  return (
    <div
      className={cn('flex items-center justify-center', BAR_CLASSES, className)}
      style={{ height, gap }}
      aria-hidden="true"
    >
      {bars.map((b, i) => (
        <span
          key={i}
          data-lk-bar
          style={{
            width: barWidth,
            height: b.h,
            background: b.c,
            borderRadius: 9999,
            display: 'inline-block',
            animation: `wv ${animDur} ease-in-out infinite alternate`,
            animationDelay: `${(i % 10) * 0.05}s`,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Static bar strip only (no LiveKit). Used as Suspense fallback while the
 * smart `AnakaBarVisualizer` chunk loads, and in Storybook.
 *
 * ## API changes (restyle US4, 2026-05-29)
 *
 * Added props:
 * - `waveform?: 'baseline'|'thinking'|'responding'` — explicit handoff preset
 *   (FR-016). When supplied, overrides any derivation from `state`. Intended
 *   for the orchestrator driving this from `mapVoiceVisualState().waveform`.
 * - `dense?: boolean` — matches handoff `dense` prop for inline vs expanded use.
 *
 * Existing props preserved unchanged:
 * - `state?: AnakaBarVisualizerViewState` — legacy LiveKit-aligned state; still
 *   fully functional so `AnakaBarVisualizer.tsx` works without any edits.
 * - `trackRef?`, `className?` — unchanged.
 *
 * The `AnakaBarVisualizerViewState` type union is unchanged, so the types test
 * at `tests/unit/components/v2/AnakaBarVisualizerView.types.test.ts` still
 * compiles without modification.
 */
export function AnakaBarVisualizerView({
  state,
  waveform,
  dense,
  className,
  trackRef: _trackRef,
}: AnakaBarVisualizerViewProps) {
  void _trackRef;
  return (
    <StaticBarFallback
      state={state}
      waveform={waveform}
      dense={dense}
      className={className}
    />
  );
}
